import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import Filters from '../components/Filters'
import KpiHeader from '../components/KpiHeader'
import ClientCard from '../components/ClientCard'
import TaskDialog from '../components/TaskDialog'
import TicketDialog from '../components/TicketDialog'
import { formatDate, formatHM, formatMoney, formatTime, monthBounds } from '../lib/format'
import type { Task, TaskFilters, WorkStatus } from '../types'
import { PAYMENT_STATUS_LABEL, PRIORITY_LABEL, WORK_STATUS_LABEL } from '../types'

/** Задача в ведомости или выплаченная закрыта для правок. */
export function isLocked(task: Task): boolean {
  return task.payment_status === 'in_payout' || task.payment_status === 'paid'
}

export const LOCKED_CONFIRM =
  'Задача уже в ведомости или выплачена, суммы по ней посчитаны. Точно хотите редактировать?'

/* «Активные» считает сервер: условие «работа не закончена ИЛИ деньги не
   закрыты» через списки статусов не выражается — они складываются через «и». */

/** Секунда тикает локально, чтобы не дёргать сервер каждую секунду. */
function useTicker(active: boolean) {
  const [, force] = useState(0)
  useEffect(() => {
    if (!active) return
    const id = setInterval(() => force((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [active])
}

type SortKey = 'date' | 'client' | 'title' | 'fact' | 'estimate' | 'amount'

/** Заголовок-переключатель: первый клик — по убыванию, второй — по возрастанию,
 *  третий возвращает исходный порядок. */
function SortTh({ label, by, sort, onSort, className }: {
  label: string
  by: SortKey
  sort: { by: SortKey; dir: 'asc' | 'desc' } | null
  onSort: (s: { by: SortKey; dir: 'asc' | 'desc' } | null) => void
  className?: string
}) {
  const active = sort?.by === by
  const next = () => {
    if (!active) return { by, dir: 'desc' as const }
    if (sort!.dir === 'desc') return { by, dir: 'asc' as const }
    return null
  }
  return (
    <th className={`sortable${className ? ' ' + className : ''}${active ? ' sorted' : ''}`}>
      <button type="button" onClick={() => onSort(next())} title="Сортировать">
        {label}
        <span className="sort-mark">{active ? (sort!.dir === 'desc' ? '↓' : '↑') : ''}</span>
      </button>
    </th>
  )
}

export default function Journal() {
  const { canSeeAll, can } = useAuth()
  const mayEditLocked = can('tasks.edit_locked')
  const qc = useQueryClient()
  const [filters, setFilters] = useState<TaskFilters>(() => {
    const { from, to } = monthBounds()
    return { date_from: from, date_to: to }
  })
  const [editing, setEditing] = useState<Task | null>(null)
  // Заявка, открытая из задачи
  const [ticketCard, setTicketCard] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  // Порядок строк. null — как отдаёт сервер: свежие сверху.
  const [sort, setSort] = useState<{ by: SortKey; dir: 'asc' | 'desc' } | null>(null)
  // Карточка клиента, открытая из строки журнала: доступы или контакты
  const [clientCard, setClientCard] = useState<
    { id: number; name: string; tab: 'accesses' | 'contacts' } | null
  >(null)

  const tasks = useQuery({
    queryKey: ['tasks', filters],
    queryFn: () => api.tasks(filters),
  })

  const kpi = useQuery({
    queryKey: ['kpi', filters],
    queryFn: () => api.kpi(filters),
  })

  const running = useQuery({
    queryKey: ['running'],
    queryFn: () => api.runningTask(),
    refetchInterval: 60_000,
  })

  useTicker(!!running.data)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tasks'] })
    qc.invalidateQueries({ queryKey: ['kpi'] })
    qc.invalidateQueries({ queryKey: ['running'] })
  }

  const start = useMutation({
    mutationFn: (id: number) => api.startTimer(id),
    onSuccess: invalidate,
    onError: (e: any) => setError(e?.message ?? 'Не удалось запустить таймер'),
  })

  const stop = useMutation({
    mutationFn: ({ id, finish }: { id: number; finish: boolean }) => api.stopTimer(id, finish),
    onSuccess: invalidate,
    onError: (e: any) => setError(e?.message ?? 'Не удалось остановить таймер'),
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status, confirm }: { id: number; status: WorkStatus; confirm: boolean }) =>
      api.changeStatus(id, status, confirm),
    onSuccess: invalidate,
    onError: (e: any) => setError(e?.message ?? 'Не удалось сменить статус'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteTask(id),
    onSuccess: invalidate,
    onError: (e: any) => setError(e?.message ?? 'Не удалось удалить задачу'),
  })

  const totalSeconds = useMemo(
    () => (tasks.data ?? []).reduce((sum, t) => sum + t.tracked_seconds, 0),
    [tasks.data],
  )

  /** Сортировка на клиенте: список за период уже пришёл целиком, гонять
   *  сервер ради порядка незачем. По умолчанию — как раньше, свежие сверху. */
  const sorted = useMemo(() => {
    const rows = [...(tasks.data ?? [])]
    if (!sort) return rows
    const value = (t: Task): string | number => {
      switch (sort.by) {
        case 'date': return t.work_date
        case 'client': return (t.client_name ?? '').toLowerCase()
        case 'title': return t.title.toLowerCase()
        case 'fact': return t.tracked_seconds
        case 'estimate': return t.estimate === null ? -1 : Number(t.estimate)
        case 'amount': return Number(t.amount ?? 0)
      }
    }
    const dir = sort.dir === 'asc' ? 1 : -1
    return rows.sort((a, b) => {
      const x = value(a)
      const y = value(b)
      if (x === y) return b.id - a.id
      return (typeof x === 'number' ? x - (y as number) : String(x).localeCompare(String(y), 'ru')) * dir
    })
  }, [tasks.data, sort])

  const runningSeconds = running.data?.running_since
    ? Math.floor((Date.now() - new Date(running.data.running_since).getTime()) / 1000)
    : 0

  // Столбцы: № + основные, плюс «Сумма» тем, кто видит деньги
  const colCount = canSeeAll ? 11 : 10
  const isActiveOnly = !!filters.active_only

  return (
    <>
      <h1 className="page-title">Журнал</h1>

      {error && (
        <div className="error" onClick={() => setError('')} role="alert">
          {error} (нажмите, чтобы скрыть)
        </div>
      )}

      <KpiHeader
        kpi={kpi.data}
        loading={kpi.isLoading}
        running={running.data}
        runningSeconds={runningSeconds}
        onStopRunning={(finish) => stop.mutate({ id: running.data!.id, finish })}
        onShowWithoutEstimate={() => setFilters({ ...filters, without_estimate: true })}
      />

      <Filters value={filters} onChange={setFilters} />

      <div className="toolbar">
        <button className="btn primary" onClick={() => setCreating(true)}>
          + Новая задача
        </button>
        <button
          className="btn"
          onClick={() => api.exportJournal(filters).catch((e) => setError(e.message))}
        >
          Выгрузить в Excel
        </button>
        <button
          className={`btn${isActiveOnly ? ' primary' : ''}`}
          onClick={() =>
            setFilters({ ...filters, active_only: isActiveOnly ? undefined : true })
          }
          title="Незакрытые задачи: работа ещё идёт или деньги не выплачены"
        >
          {isActiveOnly ? '✓ Активные' : 'Активные'}
        </button>
        {filters.without_estimate && (
          <button
            className="btn"
            onClick={() => setFilters({ ...filters, without_estimate: undefined })}
          >
            ✕ Показаны только задачи без оценки
          </button>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ color: 'var(--muted)', paddingBottom: 6 }}>
          Показано задач: {tasks.data?.length ?? 0} · {formatHM(totalSeconds)}
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="col-num">№</th>
              <SortTh label="Дата" by="date" sort={sort} onSort={setSort} />
              <SortTh label="Клиент" by="client" sort={sort} onSort={setSort} />
              <SortTh label="Задача" by="title" sort={sort} onSort={setSort} />
              <th>Время</th>
              <SortTh label="Факт" by="fact" sort={sort} onSort={setSort} className="num" />
              <SortTh label="Оценка" by="estimate" sort={sort} onSort={setSort} className="num" />
              <th>Статус</th>
              {canSeeAll && (
                <SortTh label="Сумма" by="amount" sort={sort} onSort={setSort} className="num" />
              )}
              <th className="col-timer">Таймер</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tasks.isLoading && (
              <tr>
                <td colSpan={colCount} className="empty">
                  Загрузка…
                </td>
              </tr>
            )}
            {tasks.data?.length === 0 && (
              <tr>
                <td colSpan={colCount} className="empty">
                  За выбранный период задач нет
                </td>
              </tr>
            )}
            {sorted.map((t) => (
              <TaskRow
                key={t.id}
                task={t}
                canSeeAll={canSeeAll}
                busy={start.isPending || stop.isPending}
                onStart={() => start.mutate(t.id)}
                onStop={(finish) => stop.mutate({ id: t.id, finish })}
                onStatus={(status) => {
                  const locked = isLocked(t)
                  if (locked && !mayEditLocked) {
                    setError('Задача в ведомости или выплачена — у вас нет права менять её статус')
                    return
                  }
                  if (locked && !confirm(LOCKED_CONFIRM)) return
                  setStatus.mutate({ id: t.id, status, confirm: locked })
                }}
                onOpenClient={(tab) =>
                  setClientCard({ id: t.client_id, name: t.client_name ?? '', tab })
                }
                onEdit={() => setEditing(t)}
                onDelete={() => {
                  if (confirm(`Удалить задачу «${t.title}»?`)) remove.mutate(t.id)
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {clientCard && (
        <ClientCard
          clientId={clientCard.id}
          clientName={clientCard.name}
          tab={clientCard.tab}
          onClose={() => setClientCard(null)}
        />
      )}

      {(creating || editing) && (
        <TaskDialog
          task={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onOpenTicket={setTicketCard}
        />
      )}

      {/* Заявка поверх задачи: специалист смотрит, с чего всё началось, и
          возвращается обратно — задача под ней никуда не делась. */}
      {ticketCard !== null && (
        <TicketDialog
          id={ticketCard}
          mayReply={can('tickets.reply')}
          onClose={() => setTicketCard(null)}
        />
      )}
    </>
  )
}

interface RowProps {
  task: Task
  canSeeAll: boolean
  busy: boolean
  onStart: () => void
  onStop: (finish: boolean) => void
  onStatus: (status: WorkStatus) => void
  onOpenClient: (tab: 'accesses' | 'contacts') => void
  onEdit: () => void
  onDelete: () => void
}

/** Круговая стрелка — повторяющаяся работа по договору. */
function IconContract() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-2.6-6.4" />
      <path d="M21 3v5h-5" />
    </svg>
  )
}

function TaskRow({
  task,
  canSeeAll,
  busy,
  onStart,
  onStop,
  onStatus,
  onOpenClient,
  onEdit,
  onDelete,
}: RowProps) {
  const isRunning = !!task.running_since
  const hasHistory = task.entries.length > 0
  // Подсветка «без оценки» — сигнал «не заполнено». К сопровождению он не
  // относится: там оценки не бывает по устройству расчёта, а не по забывчивости.
  const noEstimate =
    !task.is_support && (task.estimate === null || task.estimate === undefined)
  // Выплаченная или попавшая в ведомость задача закрыта: время по ней не пишется
  const isClosed = isLocked(task)
  // Работа окончена и расчёт по ней закрыт (в ведомости или выплачена) —
  // такая строка больше не требует внимания, приглушаем её
  const isSettled = task.work_status === 'done' && isClosed


  // Закрытые интервалы + текущий, досчитанный в браузере: секунды тикают без
  // обращения к серверу (перерисовку запускает useTicker выше).
  const closedSeconds = task.entries
    .filter((e) => e.stopped_at)
    .reduce((sum, e) => sum + e.seconds, 0)
  const liveSeconds = isRunning
    ? closedSeconds + Math.floor((Date.now() - new Date(task.running_since!).getTime()) / 1000)
    : task.tracked_seconds

  // В журнале — только последняя отметка. Полная история интервалов в карточке задачи.
  const lastStart = task.entries.length ? task.entries[task.entries.length - 1].started_at : null
  const closed = task.entries.filter((e) => e.stopped_at)
  const lastStop = closed.length ? closed[closed.length - 1].stopped_at : null
  const moreThanOne = task.entries.length > 1

  // Срочность заметна и без чтения: полоска у края строки для важных задач
  const urgent = task.priority === 'urgent' || task.priority === 'high'

  const rowClass = [
    isRunning ? 'running' : '',
    noEstimate ? 'no-estimate' : '',
    isSettled ? 'settled' : '',
    urgent && !isSettled ? `prio-row-${task.priority}` : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <tr className={rowClass || undefined}>
      <td className="task-num" data-label="№">{task.id}</td>
      <td className="mono nowrap" data-label="Дата">{formatDate(task.work_date)}</td>
      <td className="cell-client" data-label="Клиент">
        <span className="client-name">{task.client_name}</span>
        <span className="cell-sub">
          {[task.subclient_name, task.source_name].filter(Boolean).join(' · ') || '—'}
        </span>
        <span className="client-links">
          <button className="chip-btn" onClick={() => onOpenClient('accesses')}>
            Доступы
          </button>
          <button className="chip-btn" onClick={() => onOpenClient('contacts')}>
            Контакты
          </button>
        </span>
      </td>
      <td className="cell-task" data-label="Задача">
        <div className="task-title">
          {task.priority !== 'normal' && (
            <span className={`prio-chip prio-${task.priority}`}>
              {PRIORITY_LABEL[task.priority]}
            </span>
          )}{' '}
          {task.title}
        </div>
        {canSeeAll && task.assignee_name && (
          <span className="cell-sub">{task.assignee_name}</span>
        )}
      </td>
      <td
        className="mono nowrap"
        data-label="Время"
        title={moreThanOne ? 'Показана последняя отметка. Все интервалы — в карточке задачи' : undefined}
      >
        {lastStart ? (
          <>
            {formatTime(lastStart)}
            {lastStop ? `–${formatTime(lastStop)}` : '…'}
            {moreThanOne && <span className="more-marker">*</span>}
          </>
        ) : (
          <span className="cell-empty">—</span>
        )}
      </td>
      <td className="num mono nowrap" data-label="Факт">{formatHM(liveSeconds)}</td>
      <td className="num" data-label="Оценка">
        {/* У сопровождения оценки не бывает: она нужна для расчёта выплаты,
            а её тут нет. Вместо прочерка — знак, объясняющий пустоту. */}
        {task.is_support ? (
          <span className="support-mark" title="Сопровождение: в выплаты не идёт">
            <IconContract />
            Сопровождение
          </span>
        ) : noEstimate ? (
          <span className="cell-empty">—</span>
        ) : (
          <span className="estimate-value">{Number(task.estimate)} ч</span>
        )}
      </td>
      <td data-label="Статус">
        <select
          className="status-select"
          value={task.work_status}
          onChange={(e) => onStatus(e.target.value as WorkStatus)}
          title="Статус можно менять прямо здесь"
        >
          {Object.entries(WORK_STATUS_LABEL).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
      </td>
      {canSeeAll && (
        <td className="num cell-money" data-label="Сумма">
          {task.rate_missing ? (
            <span className="no-rate" title="У исполнителя не задана ставка — сумма не считается">
              нет ставки
            </span>
          ) : (
            <span className="mono nowrap">{formatMoney(task.amount)}</span>
          )}
          {/* Статус оплаты живёт под суммой: вместе они читаются как одна мысль */}
          <span className={`badge tiny ${task.payment_status}`}>
            {PAYMENT_STATUS_LABEL[task.payment_status]}
          </span>
        </td>
      )}
      <td className="cell-timer" data-label="Таймер">
        {isRunning ? (
          <span className="timer-actions">
            <button className="btn stop" disabled={busy} onClick={() => onStop(true)}>
              Стоп
            </button>
            <button className="btn pause" disabled={busy} onClick={() => onStop(false)}>
              Пауза
            </button>
          </span>
        ) : isClosed ? (
          <span className="closed-mark" title="Задача выплачена и закрыта">
            закрыта
          </span>
        ) : hasHistory ? (
          <button className="btn resume" disabled={busy} onClick={onStart}>
            Продолжить
          </button>
        ) : (
          <button className="btn start" disabled={busy} onClick={onStart}>
            Старт
          </button>
        )}
      </td>
      <td className="cell-actions" data-label="">
        <button className="btn small" onClick={onEdit} title="Открыть карточку задачи">
          Открыть
        </button>
        <button className="btn small danger icon" onClick={onDelete} title="Удалить задачу">
          ✕
        </button>
      </td>
    </tr>
  )
}
